import React from "react";
import { useI18n } from "../../i18n/I18nContext";
import { C } from "../../constants/design";
import { Player, LeagueMatchRecord } from "../../types/game";
import { useLeague } from "../../hooks/useLeague";
import { PlayerRating } from "../../engine/matchEngine";
import { LeagueOverview } from "./LeagueOverview";
import { LeaguePrep } from "./LeaguePrep";
import { OrbFusion } from "./OrbFusion";
import { LeagueStandings } from "./LeagueStandings";
import { LeagueDuelScreen } from "../../features/league-duel";
import type { InitialDuelData } from "../../features/league-duel/components/LeagueDuelScreen";
import { getEquipmentStats, getEquipmentEnergyBonus } from "../../features/league-duel/engine/combat";
import { playerLevel } from "../../engine/playerEngine";
import { calculateRegeneratedEnergy } from "../../features/league-duel/engine/energy";
import { rankProgress } from "../../features/league-duel/engine/duelRanking";
import { creditDuelGold } from "../../features/league-duel/engine/duelGold";
import { LAYER_IMGS } from "../../constants/items";
import { StadiumTrialScreen } from "../../features/stadium-trial";
import type { PveCompletePatch } from "../../features/stadium-trial";
import { calculatePveCondition, MAX_CONDITION, maxHpFromStats, hpFromCondition } from "../../features/stadium-trial/engine/pveEngine";

const FIVE_DUEL_IMG   = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/GameMode-FiveDuel.png";
const TEAM_LEAGUE_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/GameMode-League.png";
const STADIUM_TRIAL_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/StadiumTrial%20(1).png";

export interface LeagueWidgetProps {
  player: Player | null;
  savePlayer: (p: Player) => void;
  onDuelXpUpdate?: (xpAfter: number) => void;
  onDuelGoldEarned?: (nextMatchGold: number) => void;
  onDuelRankUpdate?: (patch: { reputation: number; mmr: number; rank: string }) => void;
  onPveComplete?: (patch: PveCompletePatch) => void;
  wallet: number;
  bettingPoints: number;
  onToast: (msg: string) => void;
  isTester?: boolean;
  onOpenPremiumPack?: () => void;
  /** Deep-link depuis le Lobby : force l'ouverture d'un écran de jeu. */
  forcedView?: "trial" | "duel" | null;
  onForcedViewConsumed?: () => void;
  /** Sortie d'une partie (Stadium Trial / Five Duel) → retour au Lobby (le hub
   *  league n'est plus dans la nav). Fallback interne sur le hub si absent. */
  onExitToLobby?: () => void;
}

type View = "hub" | "duel" | "trial" | "overview" | "prep" | "fusion" | "teams";

const TEAM_NAME = "Hyundai FC";

const resultColor = (r: string) =>
  r === "win" ? C.turf : r === "draw" ? C.gold : C.live;
const ratingColor = (r: string) =>
  Number(r) >= 7 ? C.turf : Number(r) >= 5 ? C.gold : C.live;

// ── RatingsPopup ───────────────────────────────────────────────────────────
function RatingsPopup({
  match,
  ratings,
  onClose,
}: {
  match: LeagueMatchRecord;
  ratings: PlayerRating[];
  onClose: () => void;
}) {
  const { t } = useI18n();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.88)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 24,
          border: "1.5px solid rgba(255,255,255,0.08)",
          width: "100%",
          maxWidth: 420,
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          maxHeight: "85vh",
          overflow: "hidden",
        }}
      >
        <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>
            {match.result === "win" ? "🏆" : match.result === "draw" ? "🤝" : "❌"}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>
            {match.result === "win"
              ? t("leagueRatings.victory")
              : match.result === "draw"
              ? t("leagueRatings.draw")
              : t("leagueRatings.defeat")}
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              fontFamily: "monospace",
              margin: "6px 0",
              color: resultColor(match.result),
            }}
          >
            {match.home_score} – {match.away_score}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            vs {match.opponent_name}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            overflowY: "auto",
            paddingRight: 2,
          }}
        >
          {ratings.map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: row.isMe
                  ? "rgba(45,227,107,0.08)"
                  : "rgba(255,255,255,0.03)",
                border: `1px solid ${
                  row.isMe ? "rgba(45,227,107,0.3)" : "rgba(255,255,255,0.06)"
                }`,
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: row.isBot ? "#555" : row.isMe ? C.turf : C.text,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {row.name}
                  {idx === 0 && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "rgba(245,179,1,0.15)",
                        color: C.gold,
                        borderRadius: 6,
                        padding: "1px 6px",
                        fontWeight: 700,
                      }}
                    >
                      {t("leagueRatings.mvpBadge")}
                    </span>
                  )}
                  {row.isMe && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "rgba(45,227,107,0.15)",
                        color: C.turf,
                        borderRadius: 6,
                        padding: "1px 6px",
                        fontWeight: 700,
                      }}
                    >
                      {t("leagueRatings.meBadge")}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "rgba(255,255,255,0.35)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.comment}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                  marginLeft: 10,
                }}
              >
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>
                  ⚡{row.power}
                </span>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 900,
                    color: ratingColor(row.rating),
                    background: "rgba(0,0,0,0.3)",
                    padding: "4px 10px",
                    borderRadius: 8,
                    fontFamily: "monospace",
                    minWidth: 34,
                    textAlign: "center",
                  }}
                >
                  {row.rating}
                </span>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          style={{
            background: C.turf,
            color: "#050f0a",
            border: "none",
            borderRadius: 12,
            padding: "14px",
            fontSize: 14,
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          {t("leagueRatings.continue")}
        </button>
      </div>
    </div>
  );
}

// ── Static team list (fake data — no backend team table yet) ──────────────
const PRESET_TEAMS = [
  { name: "Hyundai FC",      players: 9,  power: 312, badge: "🔵", color: "#3B82F6" },
  { name: "Seoul Dragons",   players: 7,  power: 278, badge: "🔴", color: "#EF4444" },
  { name: "Daejeon Bulls",   players: 11, power: 401, badge: "🟡", color: "#F5B301" },
  { name: "Busan Sharks",    players: 5,  power: 194, badge: "🟢", color: "#2DE36B" },
  { name: "Incheon Eagles",  players: 8,  power: 346, badge: "🟣", color: "#A855F7" },
  { name: "Gwangju Tigers",  players: 6,  power: 229, badge: "🟠", color: "#F97316" },
];

// ── Main component ─────────────────────────────────────────────────────────
export function LeagueWidget({
  player,
  savePlayer,
  onDuelXpUpdate,
  onDuelGoldEarned,
  onDuelRankUpdate,
  onPveComplete,
  wallet,
  bettingPoints,
  onToast,
  isTester,
  onOpenPremiumPack,
  forcedView,
  onForcedViewConsumed,
  onExitToLobby,
}: LeagueWidgetProps) {
  const { t } = useI18n();
  const [viewState, setView] = React.useState<View>("hub");
  // Deep-link Lobby → écran de jeu. `forcedView` prime de façon SYNCHRONE sur l'état
  // interne : la vue trial/duel s'affiche dès le 1er rendu, sans jamais peindre le
  // "hub" (fini le flash ~0.5s de l'écran des 2 jeux). L'effet ne fait que persister
  // le choix dans viewState + consommer le flag côté App.
  const view = forcedView ?? viewState;
  React.useEffect(() => {
    if (forcedView) {
      setView(forcedView);
      onForcedViewConsumed?.();
    }
  }, [forcedView]); // eslint-disable-line
  const [creating, setCreating] = React.useState(false);
  const [newTeamName, setNewTeamName] = React.useState("");
  const league = useLeague(player, savePlayer, bettingPoints);

  // ── Build home layers from equipped items ────────────────────────────────
  const homeLayers = React.useMemo(() => {
    const inv = player?.inventory ?? [];
    const eq = player?.equipped ?? {};
    const layerImg = (slot: string) => {
      const iid = eq[slot];
      const it = iid ? inv.find((x) => x.iid === iid) : null;
      return it ? (LAYER_IMGS[it.name] ?? null) : null;
    };
    return {
      ball: layerImg("ball"),
      familiar: layerImg("familiar"),
      shoes: layerImg("shoes"),
      top: layerImg("top"),
      head: layerImg("head"),
    };
  }, [player?.inventory, player?.equipped]);

  // Niveau du joueur (pour le socle de vitalité et les déblocages).
  const gameLevel = React.useMemo(
    () => playerLevel(bettingPoints + (player?.duel_xp ?? 0)),
    [bettingPoints, player?.duel_xp]
  );

  // ── Stats d'équipement du joueur ──────────────────────────────────────────
  // Incluent le socle de vitalité (HP) selon le niveau : c'est une stat de
  // personnage, donc appliquée partout (PVE, Duel, Ligue, profil).
  const playerStats = React.useMemo(
    () => getEquipmentStats(player?.inventory, player?.equipped, gameLevel),
    [player?.inventory, player?.equipped, gameLevel]
  );

  // Bonus d'énergie PVE apporté par le ballon équipé (affixe epic/legendary).
  const pveEnergyBonus = React.useMemo(
    () => getEquipmentEnergyBonus(player?.inventory, player?.equipped),
    [player?.inventory, player?.equipped]
  );

  // ── Données duel pré-chargées depuis le boot — évite le fetch on-mount ────
  const initialDuelData = React.useMemo<InitialDuelData | undefined>(() => {
    if (!player) return undefined;
    const data: InitialDuelData = {
      mmr: player.duel_mmr,
      reputation: player.duel_reputation,
      xp: player.duel_xp ?? 0,
      energy: player.duel_energy,
      energyUpdatedAt: player.duel_energy_updated_at,
      chestDate: player.duel_chest_date,
      chestMatches: player.duel_chest_matches,
      chestClaimed: player.duel_chest_claimed,
    };
    console.log("[DailyChest] mapped initialDuelData chest", {
      chestDate: data.chestDate,
      chestMatches: data.chestMatches,
      chestClaimed: data.chestClaimed,
    });
    return data;
  }, [ // eslint-disable-line
    player?.duel_mmr, player?.duel_reputation, player?.duel_xp,
    player?.duel_energy, player?.duel_energy_updated_at,
    player?.duel_chest_date, player?.duel_chest_matches, player?.duel_chest_claimed,
  ]);

  // ── Hub screen ────────────────────────────────────────────────────────────
  if (view === "hub") {
    const hubEnergy = player?.duel_energy_updated_at
      ? calculateRegeneratedEnergy(player.duel_energy ?? 0, player.duel_energy_updated_at).energy
      : (player?.duel_energy ?? null);

    const repProg = rankProgress(player?.duel_reputation ?? 0);

    return (
      <div style={{ padding: "20px 16px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Stadium Trial card ─────────────────────────────────────────── */}
        <div onClick={() => setView("trial")} style={{ cursor: "pointer" }}>
          {/* Gradient border (5px) + violet neon glow — same structure as Five Duel */}
          <div style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #B98CFF 100%)",
            borderRadius: 20,
            padding: 5,
            boxShadow: "0 0 28px 5px rgba(150,60,255,0.55), 0 0 8px 2px rgba(150,60,255,0.8)",
          }}>
            <div style={{ background: "#0B0714", borderRadius: 16, padding: "11px 11px 0 11px", overflow: "hidden", boxShadow: "inset 0 0 12px rgba(160,90,255)" }}>
              <div style={{ position: "relative", borderRadius: 10, overflow: "hidden" }}>
                {/* Cover image */}
                <img
                  src={STADIUM_TRIAL_IMG}
                  alt="Stadium Trial"
                  style={{ width: "100%", display: "block", aspectRatio: "394/190", objectFit: "cover" }}
                />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.75) 100%)", pointerEvents: "none" }} />
                {/* Current tier badge */}
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  background: "rgba(0,0,0,0.72)", borderRadius: 10,
                  padding: "5px 12px", backdropFilter: "blur(6px)",
                  fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.7)",
                }}>
                  Tier {player?.pve_current_tier ?? 1}
                </div>
                {/* Title */}
                <div style={{ position: "absolute", bottom: 14, right: 16, textShadow: "0 2px 12px rgba(0,0,0,0.9)", textAlign: "right" }}>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#fff", letterSpacing: -0.5 }}>Stadium Trial</div>
                </div>
              </div>
              {/* Subtitle row */}
              <div style={{ height: 34, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#FFAD9B", flex: 1, minWidth: 0 }}>
                  {t("leagueHub.stadiumTrialSubtitle")}
                </span>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#FF6B6B", whiteSpace: "nowrap", marginLeft: 10, flexShrink: 0 }}>
                  ❤️ {(() => {
                    const maxHp = maxHpFromStats(playerStats.vitesse);
                    const pct = player?.pve_condition_updated_at
                      ? calculatePveCondition(player?.pve_condition ?? MAX_CONDITION, player.pve_condition_updated_at).condition
                      : (player?.pve_condition ?? MAX_CONDITION);
                    return <>{Math.round(hpFromCondition(pct, maxHp))}<span style={{ opacity: 0.7 }}> / {maxHp} HP</span></>;
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Five Duel card ─────────────────────────────────────────────── */}
        <div onClick={() => setView("duel")} style={{ cursor: "pointer" }}>
          {/* Gradient border (5px) + blue neon glow */}
          <div style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #82B2FF 100%)",
            borderRadius: 20,
            padding: 5,
            boxShadow: "0 0 28px 5px rgba(0,55,255,0.55), 0 0 8px 2px rgba(0,55,255,0.8)",
          }}>
<div style={{
  background: "#05090f",
  borderRadius: 16,
  padding: "11px 11px 0 11px",
  overflow: "hidden",
  boxShadow: "inset 0 0 12px rgba(0,95,255)",
}}>              {/* Image with overlays */}
              <div style={{ position: "relative", borderRadius: 10, overflow: "hidden" }}>
                <img
                  src={FIVE_DUEL_IMG}
                  alt="Five Duel"
                  style={{ width: "100%", display: "block", aspectRatio: "394/190", objectFit: "cover" }}
                />
                {/* Diagonal gradient overlay */}
                <div style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: "linear-gradient(135deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.82) 100%)",
                }} />
                {/* Rank badge — top right */}
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  background: "rgba(0,0,0,0.72)", borderRadius: 10,
                  padding: "5px 12px", display: "flex", alignItems: "center", gap: 6,
                  backdropFilter: "blur(6px)",
                }}>
                  <span style={{ fontSize: 16, lineHeight: 1 }}>{repProg.rankIcon}</span>
<span style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>
  {repProg.progressInCurrentRank}
</span>
                </div>
                {/* Title — bottom right */}
                <div style={{
                  position: "absolute", bottom: 14, right: 16,
                  fontSize: 30, fontWeight: 700, color: "#fff", letterSpacing: -0.5,
                  textShadow: "0 2px 12px rgba(0,0,0,0.9)",
                }}>
                  Five Duel
                </div>
              </div>

              {/* Subtitle row — 34px */}
              <div style={{
                height: 34, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px",
              }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#ADCFFF", flex: 1, minWidth: 0 }}>
                  {t("leagueHub.fiveDuelSubtitle")}
                </span>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#FFF5AC", whiteSpace: "nowrap", marginLeft: 10, flexShrink: 0 }}>
                  ⚡ {hubEnergy ?? "--"}<span style={{ opacity: 0.7 }}> / 8</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Team League card — visible for aseo.bonjour only ───────────── */}
        {isTester && (
        <div
          onClick={() => setView("overview")}
          style={{ cursor: "pointer" }}
        >
          {/* Gray border (3px) */}
          <div style={{ background: "#3A3A3A", borderRadius: 20, padding: 5 }}>
            <div style={{ background: "#080808", borderRadius: 16, padding: "11px 11px 0 11px", overflow: "hidden" }}>
              {/* Image — 50% opacity */}
              <div style={{ position: "relative", borderRadius: 10, overflow: "hidden" }}>
                <img
                  src={TEAM_LEAGUE_IMG}
                  alt="Team League"
                  style={{ width: "100%", display: "block", aspectRatio: "394/190", objectFit: "cover", opacity: 0.5 }}
                />
                {/* Diagonal gradient overlay */}
                <div style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: "linear-gradient(135deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.82) 100%)",
                }} />
                {/* COMING SOON + Title — bottom right */}
                <div style={{ position: "absolute", bottom: 14, right: 16, textAlign: "right" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>
                    {t("leagueHub.comingSoon")}
                  </div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#fff", letterSpacing: -0.5 }}>
                    Team League
                  </div>
                </div>
              </div>

              {/* Subtitle row — 34px */}
              <div style={{ height: 34, display: "flex", alignItems: "center", padding: "0 2px" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255, 255, 255, 0.7)" }}>
                  {t("leagueHub.teamLeagueSubtitle")}
                </span>
              </div>
            </div>
          </div>
        </div>
        )}

      </div>
    );
  }

  // ── Stadium Trial screen ──────────────────────────────────────────────────
  if (view === "trial" && player) {
    return (
      <StadiumTrialScreen
        player={player as Player & { profile_id?: string }}
        playerStats={playerStats}
        playerLevel={gameLevel}
        energyBonus={pveEnergyBonus}
        homeLayers={homeLayers}
        onExit={onExitToLobby ?? (() => setView("hub"))}
        onComplete={(patch) => {
          onPveComplete?.(patch);
        }}
        onChestEarned={() => onOpenPremiumPack?.()}
      />
    );
  }

  // ── Solo Duel screen ──────────────────────────────────────────────────────
  if (view === "duel") {
    return (
      <LeagueDuelScreen
        playerName={player?.name ?? "You"}
        playerProfileId={player?.id ?? null}
        playerAvatar={player?.avatar}
        onExit={onExitToLobby ?? (() => setView("hub"))}
        homeLayers={homeLayers}
        playerStats={playerStats}
        playerLevel={gameLevel}
        initialDuelData={initialDuelData}
        onChestClaimed={onOpenPremiumPack}
        onGoldEarned={async (amount) => {
          if (!player) return;
          // Option A : on relit match_gold depuis Supabase (source de vérité) avant
          // de créditer, au lieu de repartir de player.match_gold local (stale →
          // régression). PATCH ciblé { match_gold } uniquement, PAS savePlayer.
          const next = await creditDuelGold(player.name, amount);
          if (next == null) {
            // Lecture/écriture DB échouée : on n'écrit PAS de valeur locale calculée.
            console.error("[Duel] gold non crédité (Supabase KO) — state local inchangé");
            return;
          }
          // Mise à jour du state local uniquement après succès DB.
          onDuelGoldEarned?.(next);
        }}
        onXpEarned={onDuelXpUpdate}
        onRankUpdate={onDuelRankUpdate}
      />
    );
  }

  // ── Join / Create screen ─────────────────────────────────────────────────
  if (!league.hasJoined) {
    if (creating) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "10px 0" }}>
          <button
            onClick={() => setCreating(false)}
            style={{ alignSelf: "flex-start", background: "transparent", border: "none", color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "4px 0" }}
          >
            {t("leagueJoin.back")}
          </button>
          <div style={{ background: "linear-gradient(160deg,#1a2035,#0f1420)", border: "1.5px solid rgba(45,227,107,0.2)", borderRadius: 20, padding: "28px 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 10, textAlign: "center" }}>🏟️</div>
            <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 900, textAlign: "center" }}>{t("leagueJoin.createTeamTitle")}</h2>
            <p style={{ color: C.muted, fontSize: 13, margin: "0 0 20px", textAlign: "center", lineHeight: 1.5 }}>
              {t("leagueJoin.createTeamSubtitle")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                placeholder={t("leagueJoin.teamNamePlaceholder")}
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
                maxLength={24}
                style={{
                  background: "rgba(255,255,255,0.07)",
                  border: "1.5px solid rgba(255,255,255,0.15)",
                  borderRadius: 12,
                  padding: "13px 14px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.text,
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={() => { if (newTeamName.trim()) league.joinLeague(newTeamName.trim()); }}
                disabled={!newTeamName.trim()}
                style={{
                  background: newTeamName.trim() ? C.turf : "rgba(255,255,255,0.07)",
                  color: newTeamName.trim() ? "#050f0a" : "rgba(255,255,255,0.3)",
                  border: "none", borderRadius: 12, padding: "14px",
                  fontSize: 15, fontWeight: 900, cursor: newTeamName.trim() ? "pointer" : "not-allowed",
                }}
              >
                {t("leagueJoin.createAndJoin")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "10px 0" }}>
        {/* Bloc intro */}
        <div style={{ background: "linear-gradient(160deg,#1a2035,#0f1420)", border: "1.5px solid rgba(45,227,107,0.15)", borderRadius: 20, padding: "28px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 8, lineHeight: 1 }}>🏆</div>
          <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>{t("leagueJoin.communityLeagueTitle")}</h2>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 auto", lineHeight: 1.6, maxWidth: 280 }}>
            {t("leagueJoin.communityLeagueDesc")}
          </p>
        </div>

        {/* Header liste équipes */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{t("leagueJoin.joinTeamHeader")}</div>
          <button
            onClick={() => setCreating(true)}
            style={{
              background: "rgba(45,227,107,0.12)",
              color: C.turf,
              border: "1px solid rgba(45,227,107,0.3)",
              borderRadius: 10,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {t("leagueJoin.createTeamButton")}
          </button>
        </div>

        {/* Liste des équipes */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {PRESET_TEAMS.map((team) => (
            <button
              key={team.name}
              onClick={() => league.joinLeague(team.name)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: C.surface,
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 14,
                padding: "12px 14px",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: "50%",
                background: `${team.color}1A`, border: `2px solid ${team.color}66`,
                display: "grid", placeItems: "center", fontSize: 20, flexShrink: 0,
              }}>
                {team.badge}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: C.text }}>{team.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {t("leagueJoin.teamMeta", team.players, team.power)}
                </div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, flexShrink: 0 }}>
                {t("leagueJoin.joinArrow")}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Ratings popup overlay ────────────────────────────────────────────────
  const ratingsPopup =
    league.showRatingsPopup && league.currentMatch ? (
      <RatingsPopup
        match={league.currentMatch}
        ratings={league.ratings}
        onClose={() => league.setShowRatingsPopup(false)}
      />
    ) : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        paddingBottom: 96,
        position: "relative",
      }}
    >
      {ratingsPopup}

      {/* Back to hub button for team league views */}
      {(view === "overview" || view === "prep" || view === "fusion" || view === "teams") && (
        <button
          onClick={() => setView("hub")}
          style={{
            alignSelf: "flex-start", background: "transparent", border: "none",
            color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer",
            padding: "4px 0 0 0",
          }}
        >
          ← League
        </button>
      )}

      {view === "overview" && (
        <LeagueOverview
          tier={league.tier}
          nextTier={league.nextTier}
          elo={league.elo}
          daysLeft={league.daysLeft}
          matchesDone={league.matchesDone}
          wins={league.wins}
          draws={league.draws}
          losses={league.losses}
          points={league.points}
          schedule={league.schedule}
          matchPowers={league.matchPowers}
          preparedCount={league.preparedCount}
          leagueMembers={league.leagueMembers}
          leagueGold={league.leagueGold}
          fieldAssets={league.fieldAssets}
          teamCombos={league.teamCombos}
          standings={league.standings}
          myRank={league.myRank}
          ready={league.ready}
          onPrepare={() => setView("prep")}
          onShowRoster={() => setView("teams")}
          onLeave={league.leaveLeague}
        />
      )}

      {view === "prep" && player && (
        <LeaguePrep
          leagueGold={league.leagueGold}
          cardState={league.cardState}
          teamCombos={league.teamCombos}
          onBuy={league.buyCard}
          onSell={league.sellCard}
          onBuyOrb={league.buyOrb}
          onReroll={league.rerollShop}
          onSendToField={league.sendToField}
          onSetLocked={league.setCardsLocked}
          onToast={onToast}
          orbCounts={league.orbCounts}
          powers={league.powers}
          onFusion={() => setView("fusion")}
          schedule={league.schedule}
          isTester={player?.name === "aseo.bonjour"}
          runTestMatch={league.runTestMatch}
          myTeamPower={league.totalTeamPower}
          resultByRound={league.resultByRound}
          scoreByRound={league.scoreByRound}
          fixtureByRound={league.fixtureByRound}
          matchHistory={league.matchHistory}
          currentMatch={league.currentMatch}
          matchPowers={league.matchPowers}
          assignPowerToMatch={league.assignPowerToMatch}
          unassignMatch={league.unassignMatch}
          goalkeeperLevel={league.goalkeeperLevel}
          upgradeGoalkeeper={league.upgradeGoalkeeper}
          wallet={wallet}
          onBack={() => setView("overview")}
        />
      )}

      {view === "fusion" && (
        <OrbFusion
          orbCounts={league.orbCounts}
          onCraft={league.craftPowerFromOrbs}
          bettingPoints={bettingPoints}
          onCraftPremium={league.craftPremiumRecipe}
          onBack={() => setView("prep")}
        />
      )}

      {view === "teams" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <button
            onClick={() => setView("overview")}
            style={{
              alignSelf: "flex-start",
              background: "transparent",
              border: "none",
              color: C.muted,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              padding: "4px 0",
            }}
          >
            {t("leagueOverview.backToLeagueOverview")}
          </button>
          <LeagueStandings
            wins={league.wins}
            draws={league.draws}
            losses={league.losses}
            played={league.played}
            points={league.points}
            winrate={league.winrate}
            totalTeamPower={league.totalTeamPower}
            goalkeeperLevel={league.goalkeeperLevel}
            leagueMembers={league.leagueMembers}
            realPlayersCount={league.realPlayersCount}
            botCount={league.botCount}
            loading={league.loading}
          />
        </div>
      )}
    </div>
  );
}
